import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { MatchResultValue } from '@openx/components-core/lib/AiChat/types';
import { useTranslation } from 'react-i18next';
import { OpenXAgentListStarIcon } from '../icons/OpenXAgentListStarIcon';
import * as styles from './MatchResultsCard.styles';

export type MatchResultItem = {
  id: string;
  name: string;
  score: number;
  metric: MatchResultValue;
};

export type ResultSummaryStat = {
  label: string;
  value: MatchResultValue;
};

type MatchResultsCardProps = {
  title: string;
  matches: MatchResultItem[];
  metricLabel: string;
  stats: ResultSummaryStat[];
  disabled?: boolean;
  dataTest: string;
};

const MATCH_SCORE_COLOR = 'primary.main';

const clampScore = (score: number) => Math.min(100, Math.max(0, score));

export const MatchResultsCard = ({
  title,
  matches,
  metricLabel,
  stats,
  disabled = false,
  dataTest,
}: MatchResultsCardProps) => {
  const { t } = useTranslation();
  const foregroundColor = disabled ? 'action.disabled' : 'text.primary';
  const iconColor = disabled ? 'action.disabled' : 'text.secondary';
  const scoreColor = disabled ? 'action.disabled' : MATCH_SCORE_COLOR;
  const progressColor = disabled ? 'action.disabled' : 'primary.main';

  return (
    <Stack data-test={dataTest} aria-disabled={disabled} sx={styles.rootSx(disabled)}>
      <Box sx={styles.cardSx}>
        <Box sx={styles.headerSx}>
          <OpenXAgentListStarIcon aria-hidden sx={styles.iconSx(iconColor)} />
          <Typography component="h3" sx={styles.titleSx(foregroundColor)}>
            {title}
          </Typography>
        </Box>

        <Box component="ol" role="list" aria-label={t('{title} ranked results', { title })} sx={styles.listSx}>
          {matches.map(match => {
            const normalizedScore = clampScore(match.score);

            return (
              <Box component="li" key={match.id} sx={styles.rowSx}>
                <Typography title={match.name} noWrap sx={styles.nameSx(foregroundColor)}>
                  {match.name}
                </Typography>

                <Box sx={styles.scoreWrapperSx}>
                  <LinearProgress
                    variant="determinate"
                    value={normalizedScore}
                    aria-label={t('{name} match', { name: match.name })}
                    sx={styles.progressSx(progressColor)}
                  />
                  <Typography sx={styles.scoreSx(scoreColor)}>{normalizedScore}%</Typography>
                </Box>

                <Typography
                  aria-label={t('{metricLabel}: {value}', { metricLabel, value: match.metric })}
                  sx={styles.metricSx(foregroundColor)}
                >
                  {match.metric}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box data-test={`${dataTest}-summary`} sx={styles.summarySx(foregroundColor)}>
        <Typography component="p" sx={styles.summaryIntroSx}>
          {t('Which translates to a high relevance score:')}
        </Typography>

        <Box component="ul" sx={styles.statListSx}>
          {stats.map(stat => (
            <Box component="li" key={stat.label} sx={styles.statRowSx}>
              <Typography component="span" sx={styles.statTermSx}>
                {stat.label}:
              </Typography>{' '}
              <Typography component="span" sx={styles.statValueSx}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
};
